import { useCallback, useEffect, useMemo } from "react";

import { NetworkStatus, useLazyQuery } from "@apollo/client";
import uniqBy from "lodash/uniqBy";

import {
  Device,
  GetDevicesQuery,
  GetDevicesQueryVariables,
} from "../../../../../API";
import { useCustomerIdGuard } from "../../../../../common/hooks/useCustomerIdGuard";
import { GET_DEVICES } from "../../../../../common/operations/queries";
import { errorNotification } from "../../../../../common/variables/notification";
import { useSelectedDataHubLocation } from "../../dataHub.variable";

export const useGetLiveViewData = () => {
  const selectedCustomerId = useCustomerIdGuard();
  const locationVariable = useSelectedDataHubLocation();

  const [getDevices, { data, fetchMore, networkStatus, loading }] =
    useLazyQuery<GetDevicesQuery, GetDevicesQueryVariables>(GET_DEVICES, {
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
    });

  useEffect(() => {
    const fetchInitialDevices = async () => {
      try {
        await getDevices({
          variables: {
            limit: 50,
            customerId: selectedCustomerId,
            locationId: locationVariable?.value,
          },
        });
      } catch (error) {
        console.error(`Error while fetching devices: ${error}`);

        errorNotification();
      }
    };

    if (selectedCustomerId) {
      fetchInitialDevices();
    }
  }, [selectedCustomerId, locationVariable?.value, getDevices]);

  const loadMoreDevices = useCallback(() => {
    if (data?.getDevices.nextToken && !loading) {
      fetchMore({
        variables: {
          limit: 50,
          customerId: selectedCustomerId,
          locationId: locationVariable?.value,
          nextToken: data.getDevices.nextToken,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;

          const uniqueDevices = uniqBy(
            [
              ...(prev.getDevices.items || []),
              ...(fetchMoreResult.getDevices.items || []),
            ],
            item => item.id
          );

          return {
            getDevices: {
              ...fetchMoreResult.getDevices,
              items: uniqueDevices,
            },
          };
        },
      }).catch(error => {
        console.error(`Error while fetching more devices: ${error}`);

        errorNotification();
      });
    }
  }, [data, fetchMore, selectedCustomerId, locationVariable?.value, loading]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      if (scrollTop + windowHeight >= documentHeight - 100) {
        loadMoreDevices(); // Trigger load more when near the bottom
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [loadMoreDevices]);

  useEffect(() => {
    loadMoreDevices();
  }, []);

  const devices: Device[] = useMemo(
    () => (data?.getDevices.items as Device[]) || [],
    [data?.getDevices.items]
  );

  // The sorting logic changes card order after fetchMore is called, so it's commented out for now.
  // const sortedDevices = [...devices]?.sort((a, b) =>
  //   a.name.localeCompare(b.name)
  // );

  return {
    devices,
    loading,
    fetchMoreLoading: networkStatus === NetworkStatus.fetchMore,
  };
};
